import React, { useState, useMemo, useRef, useEffect } from 'react';
import zipcelx from 'zipcelx';
import HighStock from 'highcharts/highstock';
import type { Options as HighchartsOptions } from 'highcharts';
import HCAccessibility from 'highcharts/modules/accessibility';
import HighchartsReact from 'highcharts-react-official';
import HCExporting from 'highcharts/modules/exporting';
import HCOfflineExporting from 'highcharts/modules/offline-exporting';
import HCExportData from 'highcharts/modules/export-data';
import HCNoData from 'highcharts/modules/no-data-to-display';
import { useQuery } from 'react-query';
import { queryOptions } from '~/shared/constants';
import { fetchIndicatorData } from '~/shared/services/api';
import moment from 'moment';
import styled from 'styled-components';
import { AlertBar, Loader } from '~/shared/components';
import { highchartsTooltipCfg } from '~/shared/config/highchartsCfg';

const DATE_DIFF = 0;

HCExporting(HighStock);
HCExportData(HighStock);
HCNoData(HighStock);
HCOfflineExporting(HighStock);
HCAccessibility(HighStock);

(function (H) {
  H.Chart.prototype.downloadXLS = function () {
    const div = document.createElement('div');
    let xlsxRows = [];
    div.style.display = 'none';
    document.body.appendChild(div);
    const rows = this.getDataRows(true);
    xlsxRows = H.map(rows.slice(1), function (row: any) {
      return H.map(row, function (column: any) {
        let tempColumn = column;
        if (typeof column === 'string' && column?.includes('00:00:00')) {
          tempColumn = moment(column).format('MM/DD/YYYY');
        }
        return {
          type: typeof column === 'number' ? 'number' : 'string',
          value: tempColumn,
        };
      });
    });

    const name = this.options.exporting!.filename;
    zipcelx({
      filename: name as unknown as string,
      sheet: {
        data: xlsxRows,
      },
    });
  };
})(HighStock);

const EMPTY_STOCK_OPTIONS: HighchartsOptions = { series: [] };

interface ISeriesObj {
  type: string;
  name: string;
  data: number[][];
  color: string;
  showInNavigator: boolean;
  legendSymbol: string;
  title?: string;
  subtitle?: string;
  ytitle?: string;
}

const Root = styled.div`
  height: 400px;
  width: 100%;
  position: relative;
  margin-top: 10px;
`;

const HighchartContainer = styled.div`
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  position: absolute;
  border: 1px solid #e6e9eb;
  background-color: var(--background-chart);
  border-radius: 4px;
  padding: 10px;
  .highcharts-button-symbol {
    width: 3.5px !important;
  }
  .highcharts-button-box {
    x: 2;
    y: 1;
    rx: 16;
    ry: 16;
    &:hover {
      fill: #d4e5ff !important;
    }
  }
  .highcharts-button-symbol {
    width: 3.5px !important;
    fill: #4c5e72 !important;
    stroke: transparent !important;
    pointer-events: none;
  }
  .highcharts-yaxis-labels {
    text {
      text-align: left;
      width: 72px !important;
      x: 0 !important;
    }
  }
`;

const AlignedLoader = styled(Loader)`
  left: calc(50% - 25px);
  top: calc(50% - 30px);
`;

HighStock.SVGRenderer.prototype.symbols.ellipsisvertical = function () {
  const path = [
    'M18.1538 16C18.1538 17.1451 17.222 18.0769 16.0769 18.0769C14.9318 18.0769 14 17.1451 14 16C14 14.8549 14.9318 13.9231 16.0769 13.9231C17.222 13.9231 18.1538 14.8549 18.1538 16Z M18.1538 22.9231C18.1538 24.0682 17.222 25 16.0769 25C14.9318 25 14 24.0682 14 22.9231C14 21.778 14.9318 20.8462 16.0769 20.8462C17.222 20.8462 18.1538 21.778 18.1538 22.9231Z M18.1538 9.07692C18.1538 10.222 17.222 11.1538 16.0769 11.1538C14.9318 11.1538 14 10.222 14 9.07692C14 7.93185 14.9318 7 16.0769 7C17.222 7 18.1538 7.93185 18.1538 9.07692Z',
  ];
  return path;
};

export default function IndicatorStackedAreaChart({
  category,
  prism,
  prismId,
  indicator,
  cellInfo,
  filterValues,
  tooltipCfg,
}: any) {
  const chartRef = useRef<any>(null);

  const [chartTitle, setChartTitle] = useState('');
  const [chartSubtitle, setChartSubtitle] = useState('');
  const [chartYtitle, setChartYtitle] = useState('');
  let exportFileName = `${cellInfo.topic ? `${cellInfo.topic}_` : ''}${
    cellInfo.symbol ? `${cellInfo.symbol}_` : ''
  }${indicator}`;
  Object.keys(filterValues).forEach(function (key) {
    if (key !== 'TimeResolution') exportFileName += `_${filterValues[key]}`;
  });

  const {
    data: apiData,
    error,
    isLoading,
  } = useQuery(
    ['fetchIndicatorData', indicator, cellInfo, JSON.stringify(filterValues)],
    async () => {
      const obj = await fetchIndicatorData(category, prism, prismId, indicator, cellInfo, filterValues);
      const colorScheme = obj.color_scheme || [];
      const xLabels = obj.row_nodes.map((r: any) => r.name);
      const yLabels = obj.col_nodes.map((c: any) => c.name);
      const seriesData: any[] = [];
      for (let j = 0; j < yLabels.length; ++j) {
        const seriesObj: ISeriesObj = {
          type: 'area',
          name: yLabels[yLabels.length - j - 1],
          data: [],
          showInNavigator: j === yLabels.length - 1,
          color: colorScheme ? colorScheme[yLabels.length - j - 1] : undefined,
          legendSymbol: 'lineMarker',
        };
        for (let i = 0; i < xLabels.length; ++i) {
          const value: number = obj.data[i][yLabels.length - j - 1] as unknown as number;
          seriesObj.data.push([moment.utc(xLabels[i]).valueOf() - DATE_DIFF, value]);
        }
        seriesObj.title = obj.title || '';
        seriesObj.subtitle = obj.subtitle || `${cellInfo.topic || ''} ${cellInfo.symbol || ''}`;
        seriesObj.ytitle = obj.ytitle || '';
        seriesData.push(seriesObj);
      }
      return { seriesData };
    },
    queryOptions,
  );

  const data = useMemo(() => apiData?.seriesData, [apiData]);

  useEffect(() => {
    if (!data) return;
    const obj = data[0];
    setChartTitle(obj.title || '');
    setChartSubtitle(obj.subtitle || `${cellInfo.topic || ''} ${cellInfo.symbol || ''} ${indicator}`);
    setChartYtitle(obj.ytitle || '');
  }, [data]);

  const chartOptions = useMemo(() => {
    if (!data) return;
    return {
      chart: {
        marginLeft: 76,
        marginRight: 30,
        marginTop: chartSubtitle ? 100 : 80,
        animation: false,
        type: 'area',
        style: {
          fontFamily: ['Montserrat', 'sans-serif'],
        },
      },
      rangeSelector: {
        buttonSpacing: 12,
        inputEnabled: false,
        selected: 5,
        floating: true,
        labelStyle: { display: 'none' },
        x: 0,
        y: chartSubtitle ? 0 : 16,
        buttonTheme: {
          r: 15,
          fill: 'transparent',
          height: 26,
          style: {
            fill: 'transparent',
            fontSize: 13,
            marginRight: 15,
            color: '#33485E',
            background: 'transparent',
            fontWeight: 500,
          },
          states: {
            hover: {
              fill: 'transparent',
              style: {
                color: '#0081C4',
                background: 'transparent',
                fontWeight: 500,
              },
            },
            select: {
              fill: 'transparent',
              style: {
                color: '#0081C4',
                background: 'transparent',
                fontWeight: 600,
              },
            },
          },
        },
        buttonPosition: {
          x: -48,
          y: -86,
        },
        buttons: [
          {
            type: 'month',
            count: 1,
            text: '1M',
            title: 'View 1 month',
          },
          {
            type: 'month',
            count: 3,
            text: '3M',
            title: 'View 3 months',
          },
          {
            type: 'month',
            count: 6,
            text: '6M',
            title: 'View 6 months',
          },
          {
            type: 'ytd',
            text: 'YTD',
            title: 'View year to date',
          },
          {
            type: 'year',
            count: 1,
            text: '1Y',
            title: 'View 1 year',
          },
          {
            type: 'all',
            text: 'Max',
            title: 'View all',
          },
        ],
      },
      exporting: {
        filename: exportFileName,
        csv: {
          columnHeaderFormatter: function (item: any) {
            if (item && item instanceof HighStock.Axis) {
              if ((item as unknown as any).type === 'datetime') {
                return 'Date';
              }
              return 'Category';
            }
            return {
              columnTitle: item.name,
            };
          },
          dateFormat: '%m/%d/%Y',
        },
        buttons: {
          contextButton: {
            x: -6,
            y: 7,
            symbol: 'ellipsisvertical',
            menuClassName: 'highcharts-indicator-menu',
            menuItems: [
              'viewFullscreen',
              'downloadPNG',
              'downloadSVG',
              'downloadPDF',
              'separator',
              'downloadXLS',
              'downloadCSV',
            ],
          },
        },
        fallbackToExportServer: false,
      },
      navigation: {
        menuItemHoverStyle: {
          background: '#EAF2FF',
        },
        menuItemStyle: {
          height: 30,
          fill: '#33485E',
          fontSize: 15,
          padding: '6px 12px',
        },
      },
      title: {
        text: chartTitle,
        align: 'left',
        x: 20,
        y: 21,
        style: {
          fontSize: '18px',
          color: 'var(--color-chart-title)',
          fontWeight: 500,
          letterSpacing: '0.5px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      },
      subtitle: {
        text: chartSubtitle,
        align: 'left',
        x: 20,
        y: 43,
        style: {
          color: 'var(--color-chart-title)',
          fontWeight: 'normal',
          letterSpacing: '0.5px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      },
      credits: {
        enabled: false,
      },
      yAxis: {
        endOnTick: false,
        showLastLabel: true,
        width: 60,
        opposite: false,
        offset: 30,
        labels: {
          x: -15,
          y: 5,
          align: 'left',
        },
        title: {
          text: chartYtitle,
          x: -19,
          style: {
            color: 'var(--color-chart-title)',
            fontWeight: 'normal',
            letterSpacing: '0.5px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        },
        plotLines: [
          {
            value: 0,
            width: 2,
            color: 'silver',
          },
        ],
      },
      legend: {
        reversed: true,
        enabled: true,
        align: 'right',
        x: -18,
        verticalAlign: 'top',
        y: chartSubtitle ? 10 : -22,
        floating: true,
        backgroundColor: 'white',
        shadow: false,
      },
      tooltip: {
        ...highchartsTooltipCfg(tooltipCfg),
        useHTML: true,
        shape: 'callout',
        backgroundColor: '#fff',
        borderRadius: 5,
        borderColor: 'transparent',
        shadow: true,
      },
      plotOptions: {
        series: {
          maxPointWidth: 30,
        },
        area: {
          stacking: 'normal',
          lineColor: 'var(--color-chart-options)',
          lineWidth: 1,
          marker: {
            lineWidth: 1,
            lineColor: 'var(--color-chart-options)',
          },
        },
      },
      series: data,
    };
  }, [data, chartTitle, chartSubtitle, chartYtitle, exportFileName, tooltipCfg]);

  useEffect(() => {
    if ((error as unknown as any)?.message) {
      chartRef.current?.showLoading((error as unknown as any)?.message);
    } else if (data?.length === 0 || !data) {
      chartRef.current?.showLoading('No Data');
    } else {
      chartRef.current?.hideLoading();
    }
  });

  return (
    <Root>
      <HighchartContainer>
        {isLoading ? (
          <AlignedLoader />
        ) : (
          <HighchartsReact
            containerProps={{ style: { height: '100%' } }}
            style={{ flex: 1 }}
            highcharts={HighStock}
            constructorType={'stockChart'}
            options={chartOptions ?? EMPTY_STOCK_OPTIONS}
            callback={(chart: any) => (chartRef.current = chart)}
          />
        )}
        {error ? <AlertBar message={(error as unknown as any).message} /> : <div />}
      </HighchartContainer>
    </Root>
  );
}
